import { useEffect, useState } from 'react'
import { Alert, Button, Image, Text, TextField } from '@cinesend/ui-kit'
import { ArrowsClockwise, Image as ImageIcon } from '@phosphor-icons/react'
import ImageModal from './image_modal'
import { humanFileSize } from '../../helpers/human_file_size'
import isEmpty from '../../helpers/is_empty'
import { useQuery, useQueryClient } from '@tanstack/react-query'

export default function ImageField({ assetID, field, value, message, saveField, refreshAsset, cacheKey }) {
  const label = field.name
  const [showImageModal, setShowImageModal] = useState(false)
  const [fileName, setFileName] = useState(value ? value : '')

  const assetImage = field.asset_image
  const missingAssetImage = assetImage && !assetImage.id

  const queryClient = useQueryClient()

  const { data: previewData } = useQuery({
    queryKey: [`/asset-images/${assetImage?.id}`],
    enabled: Boolean(assetImage?.id),
    retry: false,
    refetchOnWindowFocus: false,
  })
  const previewUrl = previewData?.asset_image?.signed_url

  useQuery({
    queryKey: [`field-values/${field.value?.id}`],
    refetchInterval: field?.value?.image_matching ? 2000 : false,
    enabled: field?.value?.image_matching === true,
    onSuccess: (data) => {
      queryClient.setQueryData(cacheKey, (oldData) => {
        if (!oldData) return oldData
        return oldData.map((item) => {
          if (item.id === data.field_id) {
            return { ...item, value: data, asset_image: data.asset_image }
          }
          return item
        })
      })
      if (data.name === 'Poster' && data.asset_image) {
        // If it's a poster and there's an image, also invalidate the standard query for posters
        const standardKey = cacheKey.concat('&isStandard=true')
        queryClient.invalidateQueries(standardKey)
      }
    },
  })

  useEffect(() => {
    if (value !== fileName && assetImage) {
      setFileName(value)
    }
  }, [value, fileName, setFileName, assetImage])

  const saveImageField = (newValue) => {
    // If field already has a value, don't overwrite it, it was set in csv (probably) to validate
    // that the correct asset is being manually selected on this form.
    // If its currently empty though, we should set a value so it has something.
    if (!isEmpty(fileName)) {
      return
    }
    refreshAsset()
    saveField(newValue ?? '')
  }

  return (
    <div className='flex w-full flex-col gap-3'>
      <div className='flex w-full items-start gap-4'>
        <div className='flex h-28 w-28 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-[color:var(--cs-color-border-default)] bg-[var(--cs-color-background-secondary)]'>
          {previewUrl ? (
            <Image src={previewUrl} alt={assetImage?.file_name || label} className='h-full w-full object-contain' />
          ) : (
            <div className='flex flex-col items-center gap-1 px-2 text-center'>
              <ImageIcon
                size={28}
                weight='regular'
                aria-hidden='true'
                className='text-[color:var(--cs-color-foreground-secondary)]'
              />
              <Text variant='xs' tone='secondary'>
                No preview
              </Text>
            </div>
          )}
        </div>
        <div className='min-w-0 flex-1 space-y-1'>
          <div className='flex items-start space-x-2'>
            <TextField
              label={field.is_required ? `${label} *` : label}
              error={message || (field.is_required && !fileName ? 'Required' : undefined)}
              placeholder={'Enter a file name to match automatically to bulk uploaded images'}
              value={fileName ?? ''}
              disabled={!!assetImage || field?.value?.image_matching}
              onChange={(e) => setFileName(e.target.value)}
              onBlur={(e) => saveField(e.target.value)} // use standard `saveField` here so value can be manually edited
            />
            {assetImage ? (
              <TextField rootClassName='max-w-24' label={'File size'} disabled value={humanFileSize(assetImage.size)} />
            ) : null}
            <Button
              disabled={field?.value?.image_matching}
              className='mt-[30px] min-w-40'
              variant='secondary'
              onClick={() => setShowImageModal(true)}
            >
              {assetImage ? 'Manage image' : 'Select image'}
            </Button>
            {showImageModal ? (
              <ImageModal
                onClose={() => setShowImageModal(false)}
                assetImage={assetImage}
                assetID={assetID}
                field={field}
                refrehAsset={refreshAsset}
                saveField={saveImageField}
              />
            ) : null}
          </div>
          {field?.value?.image_matching && (
            <Alert tone='info' size='sm' icon={<ArrowsClockwise size={20} className='animate-spin' />}>
              Image matching in progress...
            </Alert>
          )}
          {missingAssetImage && (
            <Alert tone='error' size='sm'>
              No image found for this asset. Please upload an image.
            </Alert>
          )}
        </div>
      </div>
    </div>
  )
}
